/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import React, { useState, useContext } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarDays,
  Briefcase,
  Users,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Briefcase as BusinessIcon,
  UserPlus,
  UserCheck,
  GitMerge,
  CreditCard,
  HeartHandshake,
  PlusCircle,
  Workflow,
  ClipboardCheck,
  Wallet,
  Mails,
  BookOpen,
  GraduationCap,
  ShoppingBag,
  PieChart,
  MessageSquareText,
  Shield,
  UserCog,
  ListOrdered,
  Map,
  CalendarCheck,
  FileText,
  User,
  BadgeCheck,
  Home,
  Crown,
  Users as UsersIcon,
  Megaphone,
  Settings2,
  Newspaper,
} from "lucide-react";
import { AdminContext } from "../context/AdminContext";
import { useTheme } from "../context/ThemeContext";
import { assets } from "../assets/assets";
import LogoutModal from "./LogoutModal";
import { ADMIN_ACCESS } from "../utils/adminAccess";

const Sidebar = ({
  isCollapsed,
  toggleCollapse,
  isMobileOpen,
  closeMobile,
  onUpgradeClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([
    "Book & Training",
    "Admins",
  ]);

  const { aToken, setAToken, isSuperAdmin, hasAdminAccess } =
    useContext(AdminContext);
    const { isDarkMode } = useTheme();

  const toggleSubmenu = (label) => {
    if (isCollapsed) toggleCollapse();
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  // Admin Navigation Items
  const adminNavItems = [
    {
      label: "Dashboard",
      icon: LayoutDashboard,
      path: "/admins/dashboard",
      access: ADMIN_ACCESS.DASHBOARD,
    },
    {
      label: "Caregivers",
      icon: Users,
      path: "/caregivers",
      access: ADMIN_ACCESS.CAREGIVERS,
      children: [
        { label: "All", icon: HeartHandshake, path: "/caregivers/all" },
        {
          label: "Verified ",
          icon: BadgeCheck,
          path: "/verified/caregivers",
        },
        {
          label: "Add",
          icon: UserPlus,
          path: "/caregivers/add",
        },
      ],
    },
    {
      label: "Bookings",
      icon: CalendarDays,
      path: "/bookings",
      access: ADMIN_ACCESS.BOOKINGS,
    },
    {
      label: "Service Requests",
      icon: Briefcase,
      path: "/requests",
      access: ADMIN_ACCESS.SERVICE_REQUESTS,
      children: [
        {
          label: "Managment Table",
          icon: Workflow,
          path: "/requests/manage",
        },
        {
          label: "Post",
          icon: PlusCircle,
          path: "/requests/post",
        },
      ],
    },

    {
      label: "Book & Training",
      icon: BookOpen,
      path: "/btp",
      access: ADMIN_ACCESS.BOOK_TRAINING,
      children: [
        { label: "Overview", icon: PieChart, path: "/btp/dashboard" },
        {
          label: "Training Requests",
          icon: GraduationCap,
          path: "btp/training-requests",
        },
        {
          label: "Book Purchases",
          icon: ShoppingBag,
          path: "/btp/book-purchases",
        },
      ],
    },
    {
      label: "Service Categories",
      icon: Settings2,
      path: "/categories",
      superOnly: true,
    },
    {
      label: "Contracts",
      icon: FileText,
      path: "/contracts",
      access: ADMIN_ACCESS.CONTRACTS,
    },
    {
      label: "Payouts",
      icon: Wallet,
      path: "/payouts",
      access: ADMIN_ACCESS.PAYOUTS,
    },
    {
      label: "Reviews",
      icon: MessageSquareText,
      path: "/reviews",
      access: ADMIN_ACCESS.REVIEWS,
    },

    {
      label: "Parent Hub",
      icon: Newspaper,
      path: "/parent-hub",
      access: ADMIN_ACCESS.PARENT_HUB,
      children: [
        { label: "Blog Posts", icon: FileText, path: "/parent-hub/blogs" },
        {
          label: "Categories",
          icon: ListOrdered,
          path: "/parent-hub/categories",
        },
        { label: "Analytics", icon: PieChart, path: "/parent-hub/analytics" },
        {
          label: "Activity Logs",
          icon: ClipboardCheck,
          path: "/parent-hub/activity-logs",
        },
      ],
    },

    {
      label: "Invoices",
      icon: Wallet,
      path: "/invoices",
      access: ADMIN_ACCESS.INVOICES,
      children: [
        { label: "Dashboard", icon: Workflow, path: "/invoices/dashboard" },
        {
          label: "Lists",
          icon: FileText,
          path: "/invoices/lists",
        },
        {
          label: "New",
          icon: PlusCircle,
          path: "/invoices/new",
        },
      ],
    },

    {
      label: "Vetting",
      icon: ClipboardCheck,
      path: "/vetting",
      access: ADMIN_ACCESS.VETTING,
    },

    {
      label: "Enagagement",
      icon: Megaphone,
      path: "/engage",
      access: ADMIN_ACCESS.ENGAGEMENT,
      children: [
        { label: "Coverage", icon: Map, path: "/engage/coverage" },
        {
          label: "Contact Us",
          icon: MessageSquareText,
          path: "/engage/contact-us",
        },
        {
          label: "Newsletter",
          icon: Mails,
          path: "/engage/newsletter",
        },
      ],
    },

    {
      label: "Admins",
      icon: Shield,
      path: "/admins",
      superOnly: true,
      children: [
        { label: "Add Sub-Admin", icon: UserCog, path: "/admins/add" },
        { label: "Sub-Admin List", icon: ListOrdered, path: "/admins/list" },
      ],
    },

    {
      label: "Clients",
      icon: UserCheck,
      path: "/clients",
      access: ADMIN_ACCESS.CLIENTS,
    },
    {
      label: "Matching Config",
      icon: GitMerge,
      path: "/matching/configuration",
      superOnly: true,
    },
    {
      label: "Settings",
      icon: Settings2,
      path: "/admins/settings",
    },
    { label: "Tasks", icon: CalendarCheck, path: "/tasks", superOnly: true },
    {
      label: "Agreements",
      icon: FileText,
      path: "/agreements",
      access: ADMIN_ACCESS.CONTRACTS,
    },
  ];

  const canViewAdminItem = (item) => {
    if (!aToken) return true;
    if (isSuperAdmin) return true;
    if (item.superOnly) return false;
    if (!item.access) return true;
    return typeof hasAdminAccess === "function" && hasAdminAccess(item.access);
  };

  // Determine which navigation items to show based on user role
  let navItems = adminNavItems;

  navItems = navItems.filter(canViewAdminItem);

  // Common link styles
  const linkClass = (isActive, isSubItem = false) => `
    flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 group relative
    ${
      isActive
        ? `${
            isDarkMode ? "bg-primary text-dark-text" : "bg-primary text-white"
          } shadow-lg ${isDarkMode ? "shadow-primary/20" : "shadow-primary/20"}`
        : `${
            isDarkMode
              ? "text-dark-muted hover:bg-dark-background hover:text-primary"
              : "text-text hover:bg-accent hover:text-primary"
          }`
    }
    ${isSubItem ? "text-sm pl-11" : ""}
  `;

  const handleLogout = () => {
    setShowLogoutModal(false);
    setAToken(null);
    navigate("/");
  };

  return (
    <>
      <aside
        className={`
          fixed md:static inset-y-0 left-0 z-30
          flex flex-col transition-all duration-300 ease-in-out
          ${isCollapsed ? "w-20" : "w-64"}
          ${
            isMobileOpen
              ? "translate-x-0"
              : "-translate-x-full md:translate-x-0"
          }
          ${
            isDarkMode
              ? "bg-dark-surface border-dark-border"
              : "bg-card border-accent"
          }
          border-r
        `}
      >
        {/* Header */}
        <div
          className={`h-20 flex items-center justify-between px-6 ${
            isDarkMode ? "border-dark-border" : "border-accent"
          } border-b`}
        >
          {!isCollapsed && (
            <div className={`${!isCollapsed ? "block" : "hidden"}`}>
              <img
                className="w-20 cursor-pointer"
                src={isDarkMode ? assets.admin_logo_white : assets.admin_logo}
                alt="Logo"
              />
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex flex-col items-center">
              <img
                className="w-10 h-10 cursor-pointer mb-1"
                src={isDarkMode ? assets.admin_logo_white : assets.admin_logo}
                alt="Logo"
              />
              <span
                className={`text-xs ${
                  isDarkMode ? "text-dark-muted" : "text-gray-500"
                }`}
              >
                {aToken ? "A" : "C"}
              </span>
            </div>
          )}

          <button
            onClick={toggleCollapse}
            className={`hidden md:flex p-1.5 rounded-lg absolute -right-3 top-8 shadow-sm transition-colors ${
              isDarkMode
                ? "bg-dark-background text-dark-muted hover:text-primary border-dark-border"
                : "bg-background text-gray-500 hover:text-primary border-accent"
            } border`}
          >
            {isCollapsed ? (
              <ChevronRight size={14} />
            ) : (
              <ChevronLeft size={14} />
            )}
          </button>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto py-6 px-4 space-y-6 custom-scrollbar">
          {/* Menu Section */}
          <div>
            {!isCollapsed && (
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-4 px-2 ${
                  isDarkMode ? "text-dark-muted" : "text-gray-400"
                }`}
              >
                Menu
              </p>
            )}
            <div className="space-y-2">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.children ? (
                    // Parent Item with Children
                    <div className="space-y-1">
                      <button
                        onClick={() => toggleSubmenu(item.label)}
                        className={`w-full ${linkClass(
                          location.pathname.startsWith(item.path),
                        )} justify-between`}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon
                            size={22}
                            className={isCollapsed ? "mx-auto" : ""}
                          />
                          {!isCollapsed && (
                            <span className="font-medium">{item.label}</span>
                          )}
                        </div>
                        {!isCollapsed && (
                          <ChevronDown
                            size={16}
                            className={`transition-transform duration-200 ${
                              expandedMenus.includes(item.label)
                                ? "rotate-180"
                                : ""
                            }`}
                          />
                        )}
                      </button>

                      {/* Submenu */}
                      <div
                        className={`overflow-hidden transition-all duration-300 ease-in-out ${
                          expandedMenus.includes(item.label) && !isCollapsed
                            ? "max-h-96 opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="space-y-1 pt-1">
                          {item.children.map((child) => (
                            <NavLink
                              key={child.path}
                              to={child.path}
                              onClick={closeMobile}
                              className={({ isActive }) =>
                                linkClass(isActive, true)
                              }
                            >
                              {!isCollapsed && (
                                <>
                                  <child.icon
                                    size={18}
                                    className={`${
                                      isDarkMode
                                        ? "text-dark-muted"
                                        : "text-gray-500"
                                    } opacity-70`}
                                  />
                                  <span className="font-medium">
                                    {child.label}
                                  </span>
                                </>
                              )}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Regular Item
                    <NavLink
                      to={item.path}
                      onClick={closeMobile}
                      className={({ isActive }) => linkClass(isActive)}
                    >
                      <item.icon
                        size={22}
                        className={isCollapsed ? "mx-auto" : ""}
                      />
                      {!isCollapsed && (
                        <span className="font-medium">{item.label}</span>
                      )}

                      {/* Tooltip for collapsed state */}
                      {isCollapsed && (
                        <div
                          className={`absolute left-full ml-4 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none ${
                            isDarkMode
                              ? "bg-dark-background text-dark-text"
                              : "bg-gray-800 text-white"
                          } text-xs`}
                        >
                          {item.label}
                        </div>
                      )}
                    </NavLink>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* General Section */}
          <div>
            {!isCollapsed && (
              <p
                className={`text-xs font-semibold uppercase tracking-wider mb-4 px-2 ${
                  isDarkMode ? "text-dark-muted" : "text-gray-400"
                }`}
              >
                General
              </p>
            )}
            <div className="space-y-2">
              <button
                onClick={() => setShowLogoutModal(true)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 ${
                  isDarkMode
                    ? "text-red-400 hover:bg-red-900/20"
                    : "text-red-600 hover:bg-red-50"
                } ${isCollapsed ? "justify-center" : ""}`}
              >
                <LogOut size={22} className={isCollapsed ? "mx-auto" : ""} />
                {!isCollapsed && <span className="font-medium">Logout</span>}
              </button>
            </div>
          </div>
        </div>

              </aside>

      {/* Logout Modal */}
      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default Sidebar;
